import React from 'react';
import { Form, Input, Space, Button, Tag } from 'antd';

interface ChangeRequest {
  field_name: string;
  status: string;
  old_value?: string;
  new_value?: string;
  id: number;
}

export interface CreateBusinessEmployerData {
  employer_no?: string;
  employer_no_password?: string;
  employer_account_no?: string;
  sst_id?: string;
  sst_password?: string;
  sst_account_no?: string;
  kwsp_id?: string;
  kwsp_password?: string;
  kwsp_account_no?: string;
  perkeso_id?: string;
  perkeso_password?: string;
  perkeso_account_no?: string;
  hrdc_id?: string;
  hrdc_password?: string;
  hrdc_account_no?: string;
  pcb?: string;
}

interface EmployersTabProps {
  employer: CreateBusinessEmployerData;
  updateEmployer: (field: keyof CreateBusinessEmployerData, value: string) => void;
  isEdit: boolean;
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  changeRequests?: ChangeRequest[];
  latestRequest?: Partial<CreateBusinessEmployerData> | null;
  getEmployerDisplayValue?: (field: keyof CreateBusinessEmployerData) => string;
  handleEmployerFieldChange?: (field: keyof CreateBusinessEmployerData, value: string) => void;
  handleApproveChangeRequest?: (requestId: number) => void;
  handleRejectChangeRequest?: (requestId: number) => void;
}

export const BusinessEmployersTab: React.FC<EmployersTabProps> = ({
  employer,
  updateEmployer,
  isEdit,
  isEditing = false,
  isCustomerPortal = false,
  changeRequests = [],
  latestRequest = null,
  getEmployerDisplayValue,
  handleEmployerFieldChange,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
}) => {
  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = !isEdit || isEditing;

  const getFieldCR = (field: keyof CreateBusinessEmployerData) =>
    changeRequests.find((cr) => cr.status === 'pending' && cr.field_name === `employer_${field}`);

  const renderValue = (
    field: keyof CreateBusinessEmployerData,
    value?: string,
    password?: boolean,
    placeholder?: string
  ) => {
    const cr = getFieldCR(field);
    const requestedValue = latestRequest ? (latestRequest as any)[field] : undefined;
    if (canEdit) {
      let display = value || '';
      if (cr?.new_value) display = cr.new_value;
      else if (getEmployerDisplayValue) {
        const pending = getEmployerDisplayValue(field);
        if (pending !== '') display = pending;
      }
      return (
        <div>
          <Input
            type="text"
            value={display}
            onChange={(e) => {
              const val = e.target.value;
              if (handleEmployerFieldChange) handleEmployerFieldChange(field, val);
              else updateEmployer(field, val);
            }}
            placeholder={placeholder || (password ? 'Password' : undefined)}
          />
          {requestedValue && requestedValue !== display && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
              Requested: {requestedValue as string}
            </div>
          )}
        </div>
      );
    }
    if (cr) {
      if (cr.status === 'approved') return <span>{cr.new_value || value || '-'}</span>;
      if (cr.status === 'rejected') return <span>{cr.old_value || value || '-'}</span>;
      return (
        <div>
          <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
            {cr.old_value || value || '-'}
          </span>
          <span style={{ color: '#ff9800' }}>{cr.new_value || '-'}</span>
        </div>
      );
    }
    return (
      <div>
        <span>{value || '-'}</span>
        {requestedValue && requestedValue !== value && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
            Requested: {requestedValue as string}
          </div>
        )}
      </div>
    );
  };

  const renderAction = () => {
    const pending = changeRequests.filter((cr) => cr.status === 'pending' && cr.field_name.startsWith('employer_'));
    if (pending.length === 0) return null;
    if (isCustomerPortal) return <Tag color="orange">Pending</Tag>;
    return (
      <Space>
        {pending.map((cr) => (
          <React.Fragment key={cr.id}>
            <Button
              type="primary"
              size="small"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              onClick={() => handleApproveChangeRequest && handleApproveChangeRequest(cr.id)}
            >
              Approve
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleRejectChangeRequest && handleRejectChangeRequest(cr.id)}
            >
              Reject
            </Button>
          </React.Fragment>
        ))}
      </Space>
    );
  };

  return (
    <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
            <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left', borderRight: '1px solid #f0f0f0' }}>Employer</th>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left', borderRight: '1px solid #f0f0f0' }}>SST</th>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left', borderRight: '1px solid #f0f0f0' }}>KWSP</th>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left', borderRight: '1px solid #f0f0f0' }}>PERKESO</th>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left' }}>HRDC</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {renderValue('employer_account_no', employer.employer_account_no, false, 'Account No.')}
                {renderValue('employer_no', employer.employer_no)}
                {renderValue('employer_no_password', employer.employer_no_password, true)}
              </Space>
            </td>
            <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {renderValue('sst_account_no', employer.sst_account_no, false, 'Account No.')}
                {renderValue('sst_id', employer.sst_id)}
                {renderValue('sst_password', employer.sst_password, true)}
              </Space>
            </td>
            <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {renderValue('kwsp_account_no', employer.kwsp_account_no, false, 'Account No.')}
                {renderValue('kwsp_id', employer.kwsp_id)}
                {renderValue('kwsp_password', employer.kwsp_password, true)}
              </Space>
            </td>
            <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {renderValue('perkeso_account_no', employer.perkeso_account_no, false, 'Account No.')}
                {renderValue('perkeso_id', employer.perkeso_id)}
                {renderValue('perkeso_password', employer.perkeso_password, true)}
              </Space>
            </td>
            <td style={{ padding: '12px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {renderValue('hrdc_account_no', employer.hrdc_account_no, false, 'Account No.')}
                {renderValue('hrdc_id', employer.hrdc_id)}
                {renderValue('hrdc_password', employer.hrdc_password, true)}
              </Space>
            </td>
          </tr>
          <tr>
            <td colSpan={5} style={{ padding: '12px' }}>
              {renderAction()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
